export enum BackgroundColor {
  Transparent = 'bg-transparent',
  White = 'bg-white',
  DarkBlue = 'bg-mf-blue-600',
  NavyBlue = 'bg-mf-navy',
  Blue = 'bg-mf-blue',
  SupportiveGray = 'bg-mf-gray-50',
}

// CSS colour for each BackgroundColor, mirroring the Tailwind tokens. Used to
// paint the document canvas (<html>) server-side so overscroll / rubber-band
// regions match the page background without a client-side flash.
export const BACKGROUND_COLOR_VALUE: Record<BackgroundColor, string> = {
  [BackgroundColor.Transparent]: 'transparent',
  [BackgroundColor.White]: '#ffffff',
  [BackgroundColor.DarkBlue]: '#214DA3', // mf-blue-600
  [BackgroundColor.NavyBlue]: '#0a1838', // mf-navy
  [BackgroundColor.Blue]: '#2757BA', // mf-blue
  [BackgroundColor.SupportiveGray]: '#F7FAFF', // mf-gray-50
};

// MARK: - OKLCH conversion
//
// OKLCH is a perceptually uniform colour space — equal numeric distance feels
// like equal visual distance — which makes it ideal for picking a foreground
// colour that contrasts predictably with a given background regardless of hue.
// Ported from mindfeed-swift (Colors.swift). Math constants come from Björn
// Ottosson's reference implementation: https://bottosson.github.io/posts/oklab/

const srgbToLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

const linearToSrgb = (c: number): number =>
  c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));

const linearSrgbToOklab = (
  r: number,
  g: number,
  b: number,
): [number, number, number] => {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const lp = Math.cbrt(l);
  const mp = Math.cbrt(m);
  const sp = Math.cbrt(s);

  return [
    0.2104542553 * lp + 0.793617785 * mp - 0.0040720468 * sp,
    1.9779984951 * lp - 2.428592205 * mp + 0.4505937099 * sp,
    0.0259040371 * lp + 0.7827717662 * mp - 0.808675766 * sp,
  ];
};

const oklabToLinearSrgb = (
  L: number,
  a: number,
  b: number,
): [number, number, number] => {
  const lp = L + 0.3963377774 * a + 0.2158037573 * b;
  const mp = L - 0.1055613458 * a - 0.0638541728 * b;
  const sp = L - 0.0894841775 * a - 1.291485548 * b;

  const l = lp ** 3;
  const m = mp ** 3;
  const s = sp ** 3;

  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
};

/**
 * Parse a `#RRGGBB` string into OKLCH coordinates. `L` is perceptual lightness
 * 0–1, `C` is chroma (≈ 0–0.4 in practice), `h` is hue in radians. Returns
 * `null` for malformed input.
 */
export const oklchFromHex = (
  hex: string,
): { L: number; C: number; h: number } | null => {
  const cleaned = hex.replace(/[^0-9a-fA-F]/g, '');
  if (cleaned.length < 6) return null;
  const int = parseInt(cleaned.slice(0, 6), 16);
  if (Number.isNaN(int)) return null;

  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;

  const [L, a, bLab] = linearSrgbToOklab(
    srgbToLinear(r),
    srgbToLinear(g),
    srgbToLinear(b),
  );
  return { L, C: Math.sqrt(a * a + bLab * bLab), h: Math.atan2(bLab, a) };
};

/** Build a `#RRGGBB` string from OKLCH coordinates, clamped to the sRGB cube. */
export const hexFromOklch = (L: number, C: number, h: number): string => {
  const a = C * Math.cos(h);
  const bLab = C * Math.sin(h);
  const [rLin, gLin, bLin] = oklabToLinearSrgb(L, a, bLab);
  const toByte = (c: number): string =>
    Math.round(clamp01(linearToSrgb(c)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toByte(rLin)}${toByte(gLin)}${toByte(bLin)}`;
};

// Brand ink — the default foreground for surfaces with no explicit colour.
const INK = '#161E3A';

/**
 * Picks a foreground colour that reads well on a given background. Uses the
 * OKLCH primitives above so the choice is made perceptually:
 *
 * 1. **Dark background** (`L < darkBgL`) — return pure white.
 * 2. **Near-greyscale background** (`C < 0.02`) — return brand ink (`#161E3A`).
 *    Avoids the muddy "tinted grey" you'd get by just darkening a hue-less
 *    colour.
 * 3. **Coloured background** — keep the same hue but shift lightness to
 *    `targetL` and bump chroma by `chromaBoost` (clamped to `maxChroma`).
 *    Produces a saturated, darker sibling of the background.
 *
 * Passing `null`/`undefined` yields ink, so the helper can be wired into any
 * call site without branching. Mirrors `Color.contrastingForeground` in
 * mindfeed-swift.
 */
export const contrastingForeground = (
  hex: string | null | undefined,
  {
    targetL = 0.32,
    chromaBoost = 1.1,
    darkBgL = 0.62,
    maxChroma = 0.18,
  }: {
    targetL?: number;
    chromaBoost?: number;
    darkBgL?: number;
    maxChroma?: number;
  } = {},
): string => {
  if (!hex) return INK;
  const lch = oklchFromHex(hex);
  if (!lch) return INK;

  if (lch.L < darkBgL) return '#ffffff';
  if (lch.C < 0.02) return INK;

  return hexFromOklch(targetL, Math.min(lch.C * chromaBoost, maxChroma), lch.h);
};
